import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { InputComponent } from '../../input/input.component';
import { SwitchButtonComponent } from '../../switch-button/switch-button.component';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-test-info-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    SwitchButtonComponent,
    CommonModule,
  ],
  templateUrl: './test-info-form.component.html',
  styleUrl: './test-info-form.component.css',
})
export class TestInfoFormComponent implements OnInit {
  @Input() initialData?: any;
  @Input() hideReviewOption = false;
  @Output() formReady = new EventEmitter<FormGroup>();

  form: FormGroup;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      title: ['', Validators.required],
      timeLimitMinutes: [60, [Validators.required, Validators.min(5)]],
      description: [''],
      // Barème de restitution : la note se lit sur ce total quel que soit le
      // poids des questions. 20 par défaut, comme côté serveur — un test
      // importé dont les questions valent 1 point se rend donc sur 20 sans
      // qu'on ait rien réparti.
      gradingScale: [20, [Validators.required, Validators.min(1)]],
      // Ce que le professeur saisit : la barre sur le barème (10 sur 20).
      passingOnScale: [10, [Validators.required, Validators.min(0)]],
      // Ce que les consommateurs envoient à l'API : un pourcentage. Dérivé du
      // précédent, jamais saisi directement — c'est la confusion entre les deux
      // qui faisait poser des seuils à 10 % au lieu de 50 %.
      passingPercentage: [50, [Validators.required, Validators.min(0), Validators.max(100)]],
      maxAttempts: [1, [Validators.required, Validators.min(1)]],
      showResultsImmediately: [true],
      allowReview: [true],
    });
  }

  ngOnInit(): void {
    if (this.initialData) {
      this.form.patchValue(this.initialData);
      // Un test rechargé porte un pourcentage : on le repose sur le barème pour
      // que le professeur retrouve la note qu'il avait saisie.
      const pct = this.initialData.passingPercentage;
      if (pct != null) {
        this.form.patchValue(
          { passingOnScale: Math.round((pct / 100) * this.scale) },
          { emitEvent: false },
        );
      }
    }

    // Les deux commandes qui déplacent la barre : la note saisie et le barème.
    this.form.get('passingOnScale')!.valueChanges.subscribe(() => this.syncPercentage());
    this.form.get('gradingScale')!.valueChanges.subscribe(() => this.syncPercentage());
    this.syncPercentage();

    this.formReady.emit(this.form);
  }

  /** Barème courant, 20 par défaut — un barème nul rendrait la division absurde. */
  private get scale(): number {
    return Number(this.form.get('gradingScale')?.value) || 20;
  }

  /** Équivalent en pourcentage de la note saisie, borné à [0, 100]. */
  get passingPercentage(): number {
    const onScale = Number(this.form.get('passingOnScale')?.value) || 0;
    return Math.max(0, Math.min(100, Math.round((onScale / this.scale) * 100)));
  }

  private syncPercentage(): void {
    this.form.get('passingPercentage')!.setValue(this.passingPercentage, { emitEvent: false });
  }
}
